// One place decides whether a workspace's plan needs attention, so the tenant
// banner, the agency console and the superadmin tenant list never disagree
// about who is expiring.
//
// Nothing here stops anyone working. An expired plan raises an alert and an
// operator decides - see entitlements.checkAccess, where only a manually
// suspended workspace is actually blocked.

const DAY = 86400000;
// Inside this window an upcoming expiry is worth flagging early.
const SOON_DAYS = 7;

const days = (iso) => Math.ceil((new Date(iso).getTime() - Date.now()) / DAY);
const past = (iso) => Boolean(iso) && new Date(iso).getTime() <= Date.now();
// comped_until is stored as 'infinity' for permanently free workspaces.
const forever = (iso) => Boolean(iso) && new Date(iso).getFullYear() > 2100;

/**
 * @param {object} p subscription-shaped: { status | sub_status, org_status,
 *   trial_end, comped_until, current_period_end }
 * @returns {null | { level: 'danger'|'warn', code, title, detail, days }}
 */
export function planAlert(p) {
  if (!p) return null;
  const sub = p.sub_status || p.status || null;
  const orgStatus = p.org_status || p.status_org || null;

  // Operator-set stop. Reported, never inferred.
  if (orgStatus === 'suspended' || orgStatus === 'cancelled') {
    return { level: 'danger', code: 'suspended', days: null,
      title: `Workspace ${orgStatus}`,
      detail: 'Access is switched off. Restore it from the tenant list.' };
  }

  if (forever(p.comped_until)) return null;                 // permanently free
  if (p.comped_until && !past(p.comped_until)) {
    const d = days(p.comped_until);
    return d <= SOON_DAYS
      ? { level: 'warn', code: 'comp_ending', days: d,
          title: `Complimentary access ends in ${d} day${d === 1 ? '' : 's'}`,
          detail: 'Move them to a paid plan or extend the comp.' }
      : null;
  }
  if (p.comped_until && past(p.comped_until)) {
    return { level: 'danger', code: 'comp_expired', days: days(p.comped_until),
      title: 'Complimentary access expired',
      detail: 'Still fully working - extend the comp or move them to a paid plan.' };
  }

  if (sub === 'trialing') {
    if (past(p.trial_end)) {
      return { level: 'danger', code: 'trial_expired', days: days(p.trial_end),
        title: 'Trial expired',
        detail: 'Still fully working - nothing has been switched off.' };
    }
    const d = days(p.trial_end);
    if (p.trial_end && d <= SOON_DAYS) {
      return { level: 'warn', code: 'trial_ending', days: d,
        title: `Trial ends in ${d} day${d === 1 ? '' : 's'}`,
        detail: 'Convert them to a paid plan before it lapses.' };
    }
    return null;
  }

  if (sub === 'past_due') {
    return { level: 'warn', code: 'past_due', days: null,
      title: 'Payment failed',
      detail: 'Retrying. Nothing has been switched off.' };
  }
  if (sub === 'halted' || sub === 'cancelled') {
    return { level: 'danger', code: 'lapsed', days: null,
      title: sub === 'halted' ? 'Subscription halted' : 'Subscription cancelled',
      detail: 'Still fully working - decide whether to suspend or re-activate.' };
  }

  if (sub === 'active' && past(p.current_period_end)) {
    return { level: 'danger', code: 'period_ended', days: days(p.current_period_end),
      title: 'Billing period ended without renewal',
      detail: 'Still fully working - check the payment.' };
  }
  return null;
}

// Tailwind classes for the two levels, kept beside the logic so every surface
// renders an alert the same way.
export const ALERT_STYLE = {
  danger: 'border-red-200 bg-red-50 text-red-700',
  warn:   'border-amber-200 bg-amber-50 text-amber-800',
};
